/** QuickPlan — Écran d'accueil : identité de suite, projets (les miens et partagés),
 *  création, import / export, partage ; écran d'accès refusé hors session. */
import { useEffect, useRef, useState } from 'react';
import { useProjectStore } from '../store/useProjectStore.js';
import { portalUrl } from '../api/client.js';
import ShareDialog from './ShareDialog.jsx';

const ROLE_LABEL = { editor: 'contributeur', viewer: 'lecture seule' };

function Denied({ error }) {
  const portal = portalUrl() ?? 'https://portail.quick-apps.fr/';
  const noSession = error.status === 401;
  return (
    <div className="picker">
      <h1>Quick<span className="logo-sfx">·Plan</span></h1>
      <div className="denied">
        {noSession
          ? <p>Connectez-vous au portail de la suite Quick-Apps, puis revenez : la session vaut pour toutes les applications.</p>
          : <p>{error.message}</p>}
        <a className="btn primary" href={portal}>{noSession ? 'Se connecter au portail' : 'Retour au portail'}</a>
      </div>
    </div>
  );
}

export default function ProjectPicker() {
  const me = useProjectStore((s) => s.me);
  const authError = useProjectStore((s) => s.authError);
  const loadMe = useProjectStore((s) => s.loadMe);
  const projects = useProjectStore((s) => s.projects);
  const loadProjects = useProjectStore((s) => s.loadProjects);
  const openProject = useProjectStore((s) => s.openProject);
  const createProject = useProjectStore((s) => s.createProject);
  const deleteProjectById = useProjectStore((s) => s.deleteProjectById);
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');
  const [sharing, setSharing] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    loadMe().then((ok) => { if (ok) loadProjects().catch((e) => setError(e.message)); });
  }, [loadMe, loadProjects]);

  if (authError) return <Denied error={authError} />;

  const create = async () => {
    if (!name.trim()) return;
    try { await createProject(name.trim()); } catch (e) { setError(e.message); }
  };

  const exportProject = async (p) => {
    setError('');
    try {
      const res = await fetch(`/api/projects/${p.id}/export`);
      if (!res.ok) throw new Error('Export impossible');
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `${p.name.replace(/[^\w.-]+/g, '_') || 'projet'}.quickplan.json`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch (e) { setError(e.message); }
  };

  const importFile = async (file) => {
    setError('');
    setBusy(`Import de ${file.name}…`);
    try {
      let json;
      try { json = JSON.parse(await file.text()); } catch { throw new Error('Fichier illisible : JSON attendu (.quickplan.json)'); }
      const res = await fetch('/api/projects/import', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(json) });
      const data = await res.json();
      if (!res.ok) throw new Error(data.details ? `${data.error} — ${data.details.slice(0, 3).join(' ; ')}` : (data.error ?? 'Import impossible'));
      await loadProjects();
      setBusy('');
      if (window.confirm(`Projet « ${data.name} » importé. L'ouvrir maintenant ?`)) await openProject(data.id);
    } catch (e) {
      setBusy('');
      setError(e.message);
    }
  };

  const portal = portalUrl();
  const mine = projects.filter((p) => p.mine);
  const shared = projects.filter((p) => !p.mine);

  const row = (p) => {
    const nShared = (p.sharedWith?.length ?? 0) + (p.viewers?.length ?? 0);
    return (
      <div key={p.id} className="item" onClick={() => openProject(p.id).catch((e) => setError(e.message))}>
        <span className="grow">
          {p.name}
          {!p.mine && <span className="badge" title={`Partagé par ${p.owner}`}>{p.owner} · {ROLE_LABEL[p.role] ?? p.role}</span>}
          {p.mine && nShared > 0 && <span className="badge" title={[...(p.sharedWith ?? []), ...(p.viewers ?? [])].join(', ')}>partagé ×{nShared}</span>}
        </span>
        <span className="meta">{p.instances} composant{p.instances > 1 ? 's' : ''} — {new Date(p.updatedAt).toLocaleDateString('fr-FR')}</span>
        {p.mine && <button title="Partager ce projet" onClick={(e) => { e.stopPropagation(); setSharing(p); }}>👤</button>}
        <button title="Exporter ce projet (.quickplan.json autonome)" onClick={(e) => { e.stopPropagation(); exportProject(p); }}>⤓</button>
        {p.mine && (
          <button className="danger" onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Supprimer le projet « ${p.name} » ?`)) deleteProjectById(p.id).catch((err) => setError(err.message));
          }}>✕</button>
        )}
      </div>
    );
  };

  return (
    <div className="picker">
      {me && (me.suite || portal) && (
        <div className="suite-bar">
          {portal && <a className="suite-back" href={portal} title="Retour au portail Quick-Apps">← Quick-Apps</a>}
          <span className="grow" />
          <span className="suite-user">{me.username}{me.admin ? ' (admin)' : ''}</span>
        </div>
      )}
      <h1>Quick<span className="logo-sfx">·Plan</span></h1>
      <div className="sub">Assemblages modulaires — profilés alu, meubles, baies 19" — 3D, plans, nomenclature</div>
      <div className="new-row">
        <input placeholder="Nom du nouveau projet…" value={name} onChange={(e) => setName(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()} />
        <button onClick={create}>Créer</button>
        <button title="Importer un projet exporté (.quickplan.json)" onClick={() => fileRef.current?.click()}>📥 Importer…</button>
        <input ref={fileRef} type="file" accept=".json,application/json" hidden onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; if (f) importFile(f); }} />
      </div>
      <div className="list">
        {mine.length > 0 && shared.length > 0 && <div className="list-title">Mes projets</div>}
        {mine.map(row)}
        {shared.length > 0 && <div className="list-title">Partagés avec moi</div>}
        {shared.map(row)}
        {!projects.length && <div className="sub">Aucun projet — créez-en un pour commencer ou importez un .quickplan.json.</div>}
      </div>
      {busy && <div className="sub">{busy}</div>}
      {error && <div style={{ color: 'var(--danger)' }}>{error}</div>}
      {sharing && <ShareDialog project={sharing} onClose={() => setSharing(null)} />}
    </div>
  );
}
